import { useState, type FormEvent, type CSSProperties } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '../../context/AuthContext'
import { AuthBrandHeader } from '../../components/auth/AuthBrandHeader'
import PasswordInput from '../../components/auth/PasswordInput'
import GoogleSignInButton from '../../components/auth/GoogleSignInButton'
import { joinInvite, loadInvitePreview } from '../../api/auth'

const card: CSSProperties = { maxWidth: 420, margin: '40px auto', padding: 24 }
const label: CSSProperties = { display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--ink3)', marginBottom: 6 }

export default function InviteAcceptScreen() {
  const [params] = useSearchParams()
  const token = params.get('token') || ''
  const errQ = params.get('invite_error')
  const navigate = useNavigate()
  const { user, refetch } = useAuth()
  const [name, setName] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(mapInviteError(errQ))

  const q = useQuery({
    queryKey: ['team-invite', token, user?.email || 'anon'],
    queryFn: () => loadInvitePreview(token),
    enabled: !!token,
  })
  const invite = q.data

  async function submit(e?: FormEvent) {
    e?.preventDefault()
    if (invite && !invite.has_account && !user) {
      if (password !== confirm) {
        setErr('Passwords do not match')
        return
      }
    }
    setBusy(true)
    setErr(null)
    try {
      const body = user ? {} : invite?.has_account
        ? { password }
        : { name: name.trim(), password }
      const out = await joinInvite(token, body)
      await refetch()
      navigate(out.redirect || '/dashboard?invite_accepted=1', { replace: true })
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Accept failed')
    } finally {
      setBusy(false)
    }
  }

  const mismatch = invite?.viewer === 'mismatch' || (user && invite && emailsDiffer(user.email, invite.email))
  const expired = !token || (q.isError && !invite)
  const headline = invite
    ? `You've been invited to join ${invite.company_name || 'this company'} by ${invite.inviter_name || invite.inviter_email || 'your teammate'} as ${invite.role_label || titleRole(invite.role)}`
    : 'Join this company'

  return (
    <div className="auth-page" data-testid="invite-confirm">
      <AuthBrandHeader />
      <div className="auth-card" style={card}>
        <h1 style={{ fontSize: 20, marginBottom: 12 }} data-testid="invite-headline">{headline}</h1>
        {q.isLoading && <p>Loading invite…</p>}
        {expired && !q.isLoading && (
          <p style={{ color: 'var(--red)' }} data-testid="invite-error">
            {err || (q.error instanceof Error ? q.error.message : 'This invite is invalid or has expired.')}
          </p>
        )}
        {invite && (
          <div data-testid="invite-details">
            <p style={{ fontSize: 13, color: 'var(--ink3)', margin: '0 0 16px' }}>
              Email <strong data-testid="invite-email">{invite.email}</strong>
            </p>
            {mismatch ? (
              <p style={{ color: 'var(--red)' }} data-testid="invite-error">
                This invite was sent to {invite.email}. You&apos;re signed in as {user?.email}. Sign out to continue with the invited email.
              </p>
            ) : (
              <form onSubmit={submit}>
                {!user && !invite.has_account && (
                  <label style={{ display: 'block', marginBottom: 14 }}>
                    <span style={label}>Full name</span>
                    <input data-testid="invite-name" type="text" autoComplete="name" required value={name}
                      onChange={(e) => setName(e.target.value)} className="fld-i" style={{ width: '100%', padding: '10px 12px' }} />
                  </label>
                )}
                {!user && (
                  <label style={{ display: 'block', marginBottom: 14 }}>
                    <span style={label}>Password</span>
                    <PasswordInput data-testid="invite-password" autoComplete={invite.has_account ? 'current-password' : 'new-password'}
                      required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)}
                      style={{ padding: '10px 12px', fontSize: 14 }} />
                  </label>
                )}
                {!user && !invite.has_account && (
                  <label style={{ display: 'block', marginBottom: 18 }}>
                    <span style={label}>Confirm password</span>
                    <PasswordInput data-testid="invite-password-confirm" autoComplete="new-password" required
                      value={confirm} onChange={(e) => setConfirm(e.target.value)}
                      style={{ padding: '10px 12px', fontSize: 14 }} />
                  </label>
                )}
                {(err || q.error) && (
                  <p style={{ color: 'var(--red)' }} data-testid="invite-error">
                    {err || (q.error instanceof Error ? q.error.message : 'Error')}
                  </p>
                )}
                <button className="btn b-dk" type="submit" disabled={busy} data-testid="invite-confirm-submit"
                  style={{ width: '100%', justifyContent: 'center' }}>
                  {submitLabel(busy, !!user, invite.has_account)}
                </button>
              </form>
            )}
            {!mismatch && (
              <div style={{ marginTop: 18 }}>
                <GoogleSignInButton
                  onClick={() => { window.location.href = `/api/auth/google?invite=${encodeURIComponent(token)}` }}
                  disabled={busy}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function emailsDiffer(a?: string | null, b?: string | null) {
  return String(a || '').toLowerCase() !== String(b || '').toLowerCase()
}

function titleRole(role?: string) {
  const r = String(role || 'member')
  return r.charAt(0).toUpperCase() + r.slice(1)
}

function submitLabel(busy: boolean, signedIn: boolean, hasAccount: boolean) {
  if (busy) return signedIn || hasAccount ? 'Joining…' : 'Creating account…'
  if (signedIn) return 'Join company'
  return hasAccount ? 'Sign in to join' : 'Create account and join'
}

function mapInviteError(code: string | null) {
  if (!code) return null
  if (code === 'invalid_or_expired') return 'This invite is invalid or has expired.'
  if (code === 'wrong_account') return 'This invite was sent to a different email.'
  return code
}
